import shopifyUrl1 from '@/assets/images/shopify_url1.png';
import shopifyUrl2 from '@/assets/images/shopify_url2.png';
import { useTranslation } from '@client-app/i18n';
import { CircleNotch, Info, MagnifyingGlassPlus, WarningCircle, X } from '@phosphor-icons/react';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../Settings/components/Modal';
import { normalizeStoreUrlTextInput } from './storeUrlNormalization';

export const STORE_URL_INPUT_CONFIG: Record<string, { prefix: string; suffix: string; placeholder: string }> = {
    Shopify: { prefix: 'https://', suffix: '.myshopify.com', placeholder: 'your-store' },
    BigCommerce: { prefix: 'https://store-', suffix: '.mybigcommerce.com', placeholder: 'your-store' },
};

const SHOPIFY_URL_GUIDES = [
    {
        id: 'step-1',
        label: 'STEP 1',
        image: shopifyUrl1,
        altKey: 'website.connectShopify.helpStepOneAlt',
    },
    {
        id: 'step-2',
        label: 'STEP 2',
        image: shopifyUrl2,
        altKey: 'website.connectShopify.helpStepTwoAlt',
    },
];

const VALIDATION_COPY_CONFIG = {
    already_bound: {
        tone: 'error',
        titleKey: 'website.connectShopify.validation.alreadyBound.title',
        bodyKey: 'website.connectShopify.validation.alreadyBound.body',
    },
    trial_valid: {
        tone: 'warning',
        titleKey: 'website.connectShopify.validation.trialValid.title',
        bodyKey: 'website.connectShopify.validation.trialValid.body',
    },
    trial_expired: {
        tone: 'warning',
        titleKey: 'website.connectShopify.validation.trialExpired.title',
        bodyKey: 'website.connectShopify.validation.trialExpired.body',
    },
} as const;

const TONE_CLASSNAME = {
    error: 'border border-red-100 bg-red-50 text-red-600',
    warning: 'border border-amber-100 bg-amber-50 text-amber-700',
} as const;

const TONE_ICON = {
    error: WarningCircle,
    warning: Info,
} as const;

type ValidationState = keyof typeof VALIDATION_COPY_CONFIG | null;

type ValidationCopy = {
    tone: 'error' | 'warning';
    title: string;
    body: string;
};

type ValidationOption = {
    id: ValidationState;
    label: string;
    activeClassName?: string;
};

type ValidationAction = {
    label: string;
    onClick: () => void;
};

type ExtraField = {
    key: string;
    label: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
};

interface StoreConnectionModalProps {
    isOpen: boolean;
    platform: string | null;
    storeUrl: string;
    onStoreUrlChange: (value: string) => void;
    onClose: () => void;
    onSubmit: () => void;
    submitLabel?: string;
    submitDisabled?: boolean;
    isLoading?: boolean;
    validationState?: ValidationState;
    onValidationStateChange?: (value: ValidationState) => void;
    validationOptions?: ValidationOption[];
    remainingTrialText?: string;
    validationCopyOverrides?: Partial<Record<Exclude<ValidationState, null>, ValidationCopy>>;
    validationActions?: Partial<Record<Exclude<ValidationState, null>, ValidationAction>>;
    extraFields?: ExtraField[];
}

const renderBodyWithHighlight = (body: string, remainingTrialText: string) => {
    if (!body.includes('{{remainingTrialText}}')) {
        return body;
    }

    const [before, after] = body.split('{{remainingTrialText}}');
    return (
        <>
            {before}
            <strong>{remainingTrialText}</strong>
            {after}
        </>
    );
};

const StoreConnectionModal: React.FC<StoreConnectionModalProps> = ({
    isOpen,
    platform,
    storeUrl,
    onStoreUrlChange,
    onClose,
    onSubmit,
    submitLabel,
    submitDisabled = false,
    isLoading = false,
    validationState = null,
    onValidationStateChange,
    validationOptions = [],
    remainingTrialText = '',
    validationCopyOverrides = {},
    validationActions = {},
    extraFields = [],
}) => {
    const { t } = useTranslation();
    const [previewImage, setPreviewImage] = useState<{ id: string; label: string; image: string; altKey: string } | null>(null);
    const inputConfig = platform ? STORE_URL_INPUT_CONFIG[platform] : null;
    const resolvedSubmitLabel = submitLabel || t('website.integrationsSettings.connect');
    const resolvedRemainingTrialText = remainingTrialText || t('website.connectShopify.defaultRemainingTrialText');

    const validationConfig = useMemo(() => {
        if (!validationState) return null;
        if (validationCopyOverrides[validationState]) {
            return validationCopyOverrides[validationState] || null;
        }

        const defaultCopy = VALIDATION_COPY_CONFIG[validationState];
        return {
            tone: defaultCopy.tone,
            title: t(defaultCopy.titleKey),
            body: t(defaultCopy.bodyKey),
        };
    }, [t, validationCopyOverrides, validationState]);
    const validationAction = validationState ? validationActions[validationState] || null : null;

    const handleClose = () => {
        if (isLoading) {
            return;
        }

        setPreviewImage(null);
        onClose();
    };

    const handleStoreUrlInputChange = (value: string) => {
        onStoreUrlChange(normalizeStoreUrlTextInput(value, platform));
    };

    const handleStoreUrlPaste: React.ClipboardEventHandler<HTMLInputElement> = (event) => {
        event.preventDefault();
        handleStoreUrlInputChange(event.clipboardData.getData('text'));
    };

    if (!inputConfig) {
        return null;
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title={t('website.integrationsSettings.connectToolTitle', { tool: platform || '' })}
                width={platform === 'Shopify' ? 'max-w-[860px]' : 'max-w-md'}
                variant="reference"
                animation="none"
                overlayClassName="z-[10000] bg-black/40"
                containerClassName="shadow-2xl"
                headerClassName="border-b border-gray-100 px-6 pb-4 pt-5"
                titleClassName="font-semibold tracking-tight text-text-main"
                bodyClassName="px-6 pb-6 pt-4"
                showHeaderDivider
            >
                <div className="space-y-4">
                    {platform === 'Shopify' ? (
                        <div className="rounded-2xl border border-[#E6E8F0] bg-[#F7F8FC] p-4">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E2E5F0] bg-white text-[#1A162F]">
                                    <Info size={16} weight="bold" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[14px] font-semibold text-[#1A162F]">{t('website.connectShopify.findUrlTitle')}</p>
                                    <p className="mt-1 text-[13px] font-normal leading-5 text-[#666F8D]">
                                        {t('website.connectShopify.findUrlDescriptionPrefix')} <span className="font-medium text-[#1A162F]">{t('common.settings')}</span>{t('website.connectShopify.findUrlDescriptionMiddle')} <span className="font-medium text-[#1A162F]">{t('website.connectShopify.domains')}</span>.
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {SHOPIFY_URL_GUIDES.map((guide) => (
                                    <button
                                        key={guide.id}
                                        type="button"
                                        onClick={() => setPreviewImage(guide)}
                                        className="group rounded-[18px] border border-[#E2E5F0] bg-white p-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-[#C9D0E6] hover:shadow-[0_10px_24px_rgba(26,22,47,0.08)]"
                                    >
                                        <span className="mb-2 inline-flex rounded-full bg-[#EEF1F8] px-2.5 py-1 text-[10px] font-bold tracking-[0.08em] text-[#5D6785]">
                                            {guide.id === 'step-1'
                                                ? t('website.connectShopify.stepLabel', { count: 1 })
                                                : t('website.connectShopify.stepLabel', { count: 2 })}
                                        </span>
                                        <div className="relative aspect-video overflow-hidden rounded-xl border border-[#EEF1F6] bg-[#F8FAFD]">
                                            <img
                                                src={guide.image}
                                                alt={t(guide.altKey)}
                                                className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                                            />
                                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#1A162F]/0 opacity-0 transition-all duration-200 group-hover:bg-[#1A162F]/16 group-hover:opacity-100">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/50 bg-white/88 text-[#1A162F] shadow-sm backdrop-blur-sm">
                                                    <MagnifyingGlassPlus size={18} weight="bold" />
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div>
                        <label className="mb-2 block text-sm font-semibold text-text-main">
                            {t('website.integrationsSettings.toolStoreUrl', { tool: platform || '' })}
                        </label>
                        <div className="flex items-center overflow-hidden rounded-xl border border-gray-300 bg-white transition-all focus-within:border-[#1A162F] focus-within:ring-2 focus-within:ring-[#1A162F]/20">
                            {inputConfig.prefix ? (
                                <span className="select-none whitespace-nowrap border-r border-gray-100 bg-gray-50/50 py-2.5 pl-3 pr-1 text-sm text-gray-500">
                                    {inputConfig.prefix}
                                </span>
                            ) : null}
                            <input
                                type="text"
                                value={storeUrl}
                                onChange={(event) => handleStoreUrlInputChange(event.target.value)}
                                onPaste={handleStoreUrlPaste}
                                placeholder={inputConfig.placeholder}
                                className="min-w-0 flex-1 bg-transparent px-2 py-2.5 text-sm text-text-main outline-none placeholder:text-gray-400"
                                autoFocus
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && storeUrl.trim() && !submitDisabled && !isLoading) {
                                        onSubmit();
                                    }
                                }}
                            />
                            {inputConfig.suffix ? (
                                <span className="select-none whitespace-nowrap border-l border-gray-100 bg-gray-50/50 py-2.5 pl-1 pr-3 text-sm text-gray-500">
                                    {inputConfig.suffix}
                                </span>
                            ) : null}
                        </div>
                    </div>

                    {extraFields.map((field) => (
                        <div key={field.key}>
                            <label className="mb-2 block text-sm font-semibold text-text-main">
                                {field.label}
                            </label>
                            <input
                                type="text"
                                value={field.value}
                                onChange={(event) => field.onChange(event.target.value)}
                                placeholder={field.placeholder}
                                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-text-main outline-none transition-all placeholder:text-gray-400 focus:border-[#1A162F] focus:ring-1 focus:ring-[#1A162F]"
                            />
                        </div>
                    ))}

                    {validationConfig ? (
                        <div className={clsx('flex items-start gap-2 rounded-lg p-3', TONE_CLASSNAME[validationConfig.tone])}>
                            {React.createElement(TONE_ICON[validationConfig.tone], {
                                size: 18,
                                weight: 'fill',
                                className: 'mt-0.5 shrink-0',
                            })}
                            <div className="min-w-0 flex-1">
                                <p className="text-[13px] leading-relaxed">
                                    <strong>{validationConfig.title}</strong>{' '}
                                    {renderBodyWithHighlight(validationConfig.body, resolvedRemainingTrialText)}
                                </p>
                                {validationAction ? (
                                    <button
                                        type="button"
                                        onClick={validationAction.onClick}
                                        className="mt-3 inline-flex rounded-lg bg-[#1A162F] px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[#1A162F]/90"
                                    >
                                        {validationAction.label}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    {onValidationStateChange && validationOptions.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#9AA2B8]">
                                {t('website.connectShopify.devStates')}
                            </span>
                            {validationOptions.map((option) => {
                                const isActive = validationState === option.id;
                                return (
                                    <button
                                        key={option.label}
                                        type="button"
                                        onClick={() => onValidationStateChange(option.id)}
                                        className={clsx(
                                            'rounded-[14px] px-4 py-2 text-[11px] transition-colors',
                                            isActive
                                                ? option.activeClassName || 'bg-[#E3E7F2] font-semibold text-[#1A162F]'
                                                : 'bg-[#F1F3F9] font-medium text-[#5D6785] hover:bg-[#E8ECF6]'
                                        )}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                            className="rounded-lg px-4 py-2 text-sm font-medium text-text-sub transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={onSubmit}
                            disabled={submitDisabled || isLoading}
                            className="inline-flex min-w-[96px] items-center justify-center gap-2 rounded-lg bg-[#1A162F] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1A162F]/90 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isLoading ? (
                                <>
                                    <CircleNotch size={16} weight="bold" className="animate-spin" />
                                    {t('website.connectShopify.checking')}
                                </>
                            ) : (
                                resolvedSubmitLabel
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {previewImage ? createPortal(
                <div
                    className="fixed inset-0 z-[11000] flex items-center justify-center bg-[#1A162F]/55 p-6 backdrop-blur-sm"
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        className="relative w-auto max-w-[min(92vw,1400px)] rounded-[28px] border border-white/40 bg-white p-4 shadow-[0_28px_80px_rgba(26,22,47,0.28)]"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#E6E8F0] bg-white text-[#666F8D] transition-colors hover:text-[#1A162F]"
                            onClick={() => setPreviewImage(null)}
                            aria-label={t('website.connectShopify.closeImagePreview')}
                        >
                            <X size={18} weight="bold" />
                        </button>

                        <div className="mb-3 pr-12">
                            <p className="text-[12px] font-bold tracking-[0.08em] text-[#8A93B2]">{previewImage.label}</p>
                        </div>

                        <div className="overflow-hidden rounded-[20px] border border-[#E9ECF4] bg-[#F8FAFD]">
                            <img
                                src={previewImage.image}
                                alt={t(previewImage.altKey)}
                                className="block h-auto max-h-[82vh] w-auto max-w-full object-contain"
                            />
                        </div>
                    </div>
                </div>,
                document.body
            ) : null}
        </>
    );
};

export default StoreConnectionModal;
